/**
 * @description 品类列构建器
 */
import React from 'react';
import { getIntl } from 'umi';
import ColumnAligner from '@intelligence/v3/components/TableComponents/ColumnAligner';
import { getGenreNameI18n } from '@intelligence/v3/utils/genre';
import { getTextWithHighlight } from '@intelligence/utils/commonUtils';

import type { IOptionWithChildren } from '@intelligence/components/GenreSelect/types';
import type { TableColumn } from 'tea-component';
import type { TDimension } from '../../../types';

/**
 * 品类列配置
 */
interface IGenreColumnConfig {
  /** 列key */
  key: string;
  /** 列标题 i18n key */
  headerKey: string;
  /** 列宽度 */
  width?: number;
  /** 取值字段 */
  field: keyof ICasualGenreProfileMetricData;
}

/**
 * SensorTower 品类列配置
 */
const SENSORTOWER_GENRE_COLUMNS: IGenreColumnConfig[] = [
  { key: 'mainGenre', headerKey: 'dbi_casual_table_col_main_genre', width: 150, field: 'sensortower_genre' },
  { key: 'subGenre', headerKey: 'dbi_casual_table_col_sub_genre', width: 150, field: 'sensortower_sub_genre' },
];

/**
 * AppMagic 品类列配置
 */
const APPMAGIC_GENRE_COLUMNS: IGenreColumnConfig[] = [
  { key: 'superGenre', headerKey: 'dbi_casual_table_col_super_genre', width: 140, field: 'super_genre' },
  { key: 'majorCategory', headerKey: 'dbi_casual_table_col_level1_genre', width: 140, field: 'major_category' },
  { key: 'midCategory', headerKey: 'dbi_casual_table_col_level2_genre', width: 140, field: 'mid_category' },
  { key: 'subCategory', headerKey: 'dbi_casual_table_col_level3_genre', width: 140, field: 'sub_category' },
];

/**
 * 构建品类列所需的参数
 */
interface IBuildGenreColumnOptions {
  /** 当前语言环境 */
  locale: string;
  /** 品类选项数据 map */
  genreOptionsMap: IRecord<IOptionWithChildren[]>;
  /** 品类数据源 */
  genreSource: IGenreSource;
  /** 品类维度 */
  genreDimension: TDimension;
  /** 搜索关键字（用于高亮显示） */
  keyword?: string;
}

/**
 * 构建品类列
 */
const buildGenreColumn = (
  config: IGenreColumnConfig,
  options: IBuildGenreColumnOptions,
): TableColumn<ICasualGenreProfileMetricData> => {
  const { key, headerKey, width = 150, field } = config;
  const { locale, genreOptionsMap, genreSource, genreDimension, keyword } = options;
  const { formatMessage } = getIntl();
  const header = formatMessage({ id: headerKey });

  return {
    key,
    fixed: 'left' as const,
    header: (
      <ColumnAligner align="left" width={width}>
        <span>{header}</span>
      </ColumnAligner>
    ),
    width,
    render: (record: ICasualGenreProfileMetricData) => {
      const value = record[field] as string | undefined;

      // 使用翻译函数获取品类名称
      const displayName = (() => {
        if (!value) {
          return '';
        }

        return getGenreNameI18n({
          locale,
          genreOptionsMap,
          genreSource,
          genreValue: value,
        });
      })();

      // 如果有搜索关键字，进行高亮处理
      const renderedText =
        keyword && displayName
          ? getTextWithHighlight(displayName, keyword, {
            isSeparate: false,
            isIgnoreCase: true,
            className: 'dbi-genre-table-highlight',
            })
          : displayName;

      return (
        <ColumnAligner align="left" width={width}>
          <span>{renderedText || '-'}</span>
        </ColumnAligner>
      );
    },
  };
};

/**
 * 根据数据源和维度获取需要显示的品类列索引范围
 * @param source 品类数据源
 * @param dimension 维度值
 * @returns 列索引范围 [startIndex, endIndex]（包含）
 */
const getVisibleColumnRange = (source: IGenreSource, dimension: TDimension): [number, number] => {
  if (source === 'sensortower') {
    switch (dimension) {
      case 'mainGenre':
        return [0, 0]; // 只显示 Main Genre
      case 'subGenre':
        return [0, 1]; // 显示 Main Genre + Sub Genre
      default:
        return [0, 0];
    }
  }

  // appmagic
  switch (dimension) {
    case 'superGenre':
      return [0, 0]; // 只显示 Super Genre
    case 'majorCategory':
      return [0, 1]; // Super Genre + Level-1 Genre
    case 'midCategory':
      return [0, 2]; // Super Genre + Level-1 Genre + Level-2 Genre
    case 'subCategory':
      return [0, 3]; // 全部显示
    default:
      return [0, 0];
  }
};

/**
 * 根据数据源和维度获取需要显示的品类列
 * @param locale 当前语言环境
 * @param genreOptionsMap 品类选项数据 map
 * @param source 品类数据源
 * @param dimension 维度值
 * @param keyword 搜索关键字（用于高亮显示）
 * @returns 品类列配置数组
 */
export const getVisibleGenreColumns = (
  locale: string,
  genreOptionsMap: IRecord<IOptionWithChildren[]>,
  source: IGenreSource,
  dimension: TDimension,
  keyword?: string,
): TableColumn<ICasualGenreProfileMetricData>[] => {
  const columnConfigs = source === 'sensortower' ? SENSORTOWER_GENRE_COLUMNS : APPMAGIC_GENRE_COLUMNS;
  const [startIndex, endIndex] = getVisibleColumnRange(source, dimension);

  const options: IBuildGenreColumnOptions = {
    locale,
    genreOptionsMap,
    genreSource: source,
    genreDimension: dimension,
    keyword,
  };

  return columnConfigs.slice(startIndex, endIndex + 1).map((config) => buildGenreColumn(config, options));
};

export default getVisibleGenreColumns;
